import { DashboardState, GraphDirectoryItem } from '@/redux/slices/slice_types';
import AverageTimes from './Widgets/Default/AverageTimes/AverageTimes';
import CompletedJobs from './Widgets/Default/CompletedJobs';
import DailyReports from './Widgets/Default/DailyReports/DailyReports';
import OpenJobStatus from './Widgets/Default/OpenJobStatus';
import OnTimeDelivery from './Widgets/Default/OnTimeDelivery/OnTimeDelivery';
import OutstandingAssets from './Widgets/Default/OutstandingAssets/OutstandingAssets';
import RecentTasks from './Widgets/Default/RecentTasks/RecentTasks';
import StatusCards from './StatusCards/StatusCards';

export type GraphListItem = {
  id: string;
  label: string; // Label seen on 'editable' view
  render: () => JSX.Element;
};

export enum PreBuiltGraphs {
  STATUS_CARDS = 'status-cards',
  OPEN_JOB_STATUS = 'open-job-status',
  COMPLETED_JOBS = 'completed-jobs',
  AVERAGE_TIMES = 'average-times',
  DAILY_REPORTS = 'daily-reports',
  ON_TIME_DELIVERY = 'on-time-delivery',
  OUTSTANDING_ASSETS = 'outstanding-assets',
  RECENT_TASKS = 'recent-tasks'
}

export const defaultGraphs = [
  // StatusCards are added before the rest of the graphs
  {
    id: PreBuiltGraphs.OPEN_JOB_STATUS as string,
    checked: true
  },
  {
    id: PreBuiltGraphs.COMPLETED_JOBS as string,
    checked: true
  },
  {
    id: PreBuiltGraphs.AVERAGE_TIMES as string,
    checked: true
  },
  {
    id: PreBuiltGraphs.DAILY_REPORTS as string,
    checked: true
  }
];

export const graphDirectory: Record<string, GraphDirectoryItem> = {
  [PreBuiltGraphs.STATUS_CARDS]: {
    id: PreBuiltGraphs.STATUS_CARDS,
    label: 'Status Cards',
    fullRow: true,
    selfManagedEditableView: true,
    render: () => <StatusCards />
  },

  [PreBuiltGraphs.OPEN_JOB_STATUS]: {
    id: PreBuiltGraphs.OPEN_JOB_STATUS,
    label: 'Open Job Status',
    render: () => <OpenJobStatus title="Open Job Status" caption="All job statuses currently unfulfilled" />
  },
  [PreBuiltGraphs.ON_TIME_DELIVERY]: {
    id: PreBuiltGraphs.ON_TIME_DELIVERY,
    label: 'On Time Delivery',
    render: () => <OnTimeDelivery title="On Time Delivery" caption="Comparison of on-time delivery over the past 6 months" />
  },
  [PreBuiltGraphs.COMPLETED_JOBS]: {
    id: PreBuiltGraphs.COMPLETED_JOBS,
    label: 'Completed Jobs',
    render: () => <CompletedJobs title="Completed Jobs" caption="Overview of total jobs completed by type" />
  },
  [PreBuiltGraphs.AVERAGE_TIMES]: {
    id: PreBuiltGraphs.AVERAGE_TIMES,
    label: 'Average Times',
    render: () => <AverageTimes title="Average Times" caption="Overview of the average completion times for Edit and Review Tasks" />
  },
  [PreBuiltGraphs.DAILY_REPORTS]: {
    id: PreBuiltGraphs.DAILY_REPORTS,
    label: 'Daily Reports',
    render: () => <DailyReports title="Reports" caption="All Reports" />
  },
  [PreBuiltGraphs.OUTSTANDING_ASSETS]: {
    id: PreBuiltGraphs.OUTSTANDING_ASSETS,
    label: 'Outstanding Assets',
    render: () => <OutstandingAssets title="Outstanding Assets" caption="Overview of the current outstanding assets within 7 days" />
  },
  [PreBuiltGraphs.RECENT_TASKS]: {
    id: PreBuiltGraphs.RECENT_TASKS,
    label: 'Recent Tasks Overview',
    render: () => <RecentTasks title="Tasks Overview" caption="An overview of recent tasks" />
  }
};

export const graphDirectoryKeys = Object.keys(graphDirectory);

export const buildGraphsOrderDefault = (list: DashboardState['graphs']['readOnly']['list']) => {
  const order: string[][] = [];
  let currentRow = 0;

  const nonFullRowList: any[] = [];
  const fullRowList: any[] = [];

  list.forEach((item, index) => {
    if (item.fullRow) {
      // save the index its at, so we can add it back in the same spot
      fullRowList.push({ item: item, index: index });
    } else {
      nonFullRowList.push(item);
    }
  });

  // build traditional checked/unchecked graph rows
  nonFullRowList.forEach((item) => {
    if (!item.checked) return;

    if (order[currentRow] === undefined) {
      order[currentRow] = [item.id];
    } else if (order[currentRow].length < 2) {
      // default is 2 graphs on every row, staggered
      order[currentRow].push(item.id);
    } else {
      currentRow++;
      order[currentRow] = [item.id];
    }
  });

  // slice the fullRow back in at the modded index of its original position
  fullRowList.forEach((item) => {
    if (!item.item.checked) return;
    const insertIndex = Math.floor(item.index / 2);
    order.splice(insertIndex, 0, [item.item.id]);
  });

  return order;
};

export const buildGraphsOrderChanged = (list: DashboardState['graphs']['readOnly']['list'], order: DashboardState['graphs']['readOnly']['order']) => {
  // map the list to an object for easier lookup
  const mappedList: Record<string, boolean> = {};
  list.forEach((item) => {
    mappedList[item.id] = item.checked;
  });

  // filter out the items that are no longer checked
  const newOrder = order
    .map((row) =>
      row.filter((id) => {
        const keep = mappedList[id];
        if (keep) delete mappedList[id];
        return keep;
      })
    )
    .filter((row) => row.length); // if removed the only item in a row, remove the row

  // add the items that are now checked to end of the list
  const newItems = Object.keys(mappedList).filter((id) => mappedList[id]);
  if (newItems.length) {
    let currentRow = newOrder.length - 1;

    newItems.forEach((id) => {
      if (newOrder[currentRow] === undefined) {
        newOrder[currentRow] = [id];
      } else if (newOrder[currentRow].length < 2) {
        // default is 2 graphs on every row, staggered
        newOrder[currentRow].push(id);
      } else {
        currentRow++;
        newOrder[currentRow] = [id];
      }
    });
  }

  return newOrder;
};
